import { useCallback, useEffect, useState } from "react";
import { getDataClient } from "@/lib/dataClient";

export interface HeatDay {
  date: string;
  minutes: number;
  dayOfWeek: number;
}

export interface TrendPoint {
  name: string;
  net: number;
  date: string;
}

export interface Badge {
  id: string;
  label: string;
  description: string;
  icon: string;
  earned: boolean;
  progress: number; // 0-100
  current: number;
  target: number;
}

export interface MotivationData {
  streak: number;
  totalMinutes: number;
  totalExams: number;
  avgNet: number;
  xp: number;
  level: number;
  levelTitle: string;
  levelProgress: number;
  xpToNext: number;
  heatmap: HeatDay[];
  trend: TrendPoint[];
  badges: Badge[];
}

const EMPTY: MotivationData = {
  streak: 0,
  totalMinutes: 0,
  totalExams: 0,
  avgNet: 0,
  xp: 0,
  level: 1,
  levelTitle: "Çaylak",
  levelProgress: 0,
  xpToNext: 500,
  heatmap: [],
  trend: [],
  badges: [],
};

const LEVEL_TITLES = [
  "Çaylak",
  "Öğrenci",
  "Azimli",
  "Çalışkan",
  "Usta",
  "Uzman",
  "Efsane",
  "Şampiyon",
];

function levelTitle(level: number): string {
  return LEVEL_TITLES[Math.min(level - 1, LEVEL_TITLES.length - 1)];
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

export function useMotivation() {
  const [data, setData] = useState<MotivationData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dataClient = getDataClient();
      const { data: authData } = await dataClient.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) {
        setData(EMPTY);
        setLoading(false);
        return;
      }

      const [sessionRes, examRes] = await Promise.all([
        dataClient
          .from("study_sessions")
          .select("minutes, studied_on")
          .eq("user_id", userId)
          .order("studied_on", { ascending: true }),
        dataClient
          .from("exam_attempts")
          .select("exam_name, net, taken_on")
          .eq("user_id", userId)
          .order("taken_on", { ascending: true }),
      ]);

      if (sessionRes.error) throw sessionRes.error;
      if (examRes.error) throw examRes.error;

      const sessions = (sessionRes.data ?? []) as Array<{ minutes: number; studied_on: string }>;
      const exams = (examRes.data ?? []) as Array<{ exam_name: string; net: number; taken_on: string }>;

      // Toplam çalışma dakikası + günlük harita
      const minuteMap = new Map<string, number>();
      let totalMinutes = 0;
      sessions.forEach((s) => {
        const m = s.minutes ?? 0;
        totalMinutes += m;
        minuteMap.set(s.studied_on, (minuteMap.get(s.studied_on) ?? 0) + m);
      });

      // Streak hesabı
      const studyDates = new Set(sessions.map((s) => s.studied_on));
      const today = new Date();
      const cursor = new Date(today);
      if (!studyDates.has(iso(cursor))) {
        cursor.setDate(cursor.getDate() - 1);
      }
      let streak = 0;
      while (studyDates.has(iso(cursor))) {
        streak += 1;
        cursor.setDate(cursor.getDate() - 1);
      }

      // Deneme istatistikleri
      const totalExams = exams.length;
      const totalNet = exams.reduce((sum, e) => sum + Number(e.net ?? 0), 0);
      const avgNet = totalExams > 0 ? Math.round((totalNet / totalExams) * 10) / 10 : 0;

      // XP & seviye
      const xp = totalMinutes + totalExams * 50 + Math.round(totalNet * 2);
      const level = Math.floor(xp / 500) + 1;
      const xpToNext = level * 500;
      const levelProgress = Math.min(100, Math.round(((xp - (level - 1) * 500) / 500) * 100));

      // Isı haritası: son 20 hafta
      const heatmap: HeatDay[] = [];
      for (let i = 139; i >= 0; i -= 1) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = iso(d);
        heatmap.push({ date: key, minutes: minuteMap.get(key) ?? 0, dayOfWeek: d.getDay() });
      }

      // Deneme trendi
      const trend: TrendPoint[] = exams.map((e) => ({
        name: e.exam_name,
        net: Number(e.net ?? 0),
        date: e.taken_on,
      }));

      // Rozetler
      const badges: Badge[] = [
        {
          id: "ilk_adim",
          label: "İlk Adım",
          description: "İlk çalışma oturumunu tamamla",
          icon: "ri-footprint-line",
          earned: totalMinutes >= 1,
          progress: Math.min(100, Math.round((totalMinutes / 1) * 100)),
          current: Math.min(totalMinutes, 1),
          target: 1,
        },
        {
          id: "caliskan",
          label: "Çalışkan",
          description: "Toplam 10 saat çalış",
          icon: "ri-time-line",
          earned: totalMinutes >= 600,
          progress: Math.min(100, Math.round((totalMinutes / 600) * 100)),
          current: totalMinutes,
          target: 600,
        },
        {
          id: "maratoncu",
          label: "Maratoncu",
          description: "Toplam 50 saat çalış",
          icon: "ri-run-line",
          earned: totalMinutes >= 3000,
          progress: Math.min(100, Math.round((totalMinutes / 3000) * 100)),
          current: totalMinutes,
          target: 3000,
        },
        {
          id: "deneme_cozen",
          label: "Deneme Çözen",
          description: "İlk deneme sınavını çöz",
          icon: "ri-file-text-line",
          earned: totalExams >= 1,
          progress: Math.min(100, Math.round((totalExams / 1) * 100)),
          current: Math.min(totalExams, 1),
          target: 1,
        },
        {
          id: "sinav_ustasi",
          label: "Sınav Ustası",
          description: "10 deneme sınavı çöz",
          icon: "ri-award-line",
          earned: totalExams >= 10,
          progress: Math.min(100, Math.round((totalExams / 10) * 100)),
          current: totalExams,
          target: 10,
        },
        {
          id: "seri",
          label: "Seri Bağımlısı",
          description: "7 gün üst üste çalış",
          icon: "ri-fire-line",
          earned: streak >= 7,
          progress: Math.min(100, Math.round((streak / 7) * 100)),
          current: streak,
          target: 7,
        },
        {
          id: "efsane_seri",
          label: "Efsane Seri",
          description: "30 gün üst üste çalış",
          icon: "ri-flashlight-line",
          earned: streak >= 30,
          progress: Math.min(100, Math.round((streak / 30) * 100)),
          current: streak,
          target: 30,
        },
        {
          id: "net_avcisi",
          label: "Net Avcısı",
          description: "Ortalama 20 nete ulaş (en az 5 deneme)",
          icon: "ri-crosshair-line",
          earned: totalExams >= 5 && avgNet >= 20,
          progress: totalExams >= 5
            ? Math.min(100, Math.round((avgNet / 20) * 100))
            : 0,
          current: avgNet,
          target: 20,
        },
      ];

      setData({
        streak,
        totalMinutes,
        totalExams,
        avgNet,
        xp,
        level,
        levelTitle: levelTitle(level),
        levelProgress,
        xpToNext,
        heatmap,
        trend,
        badges,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Veriler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}